import { datadogRum } from "@datadog/browser-rum";
import { EHR_SYNC_OUTBOUND_GATED_MESSAGE } from "@utils/ehrActions";
import React, { useRef, useState } from "react";

import { Button, Dialog, Paragraph, Tooltip } from "@fronterahealth/frontera-ui-components";

import { ApiReportReportTypeChoices, useSendClientToFronteraEhrMutation } from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";

export const SEND_CLIENT_TO_FRONTERA_EHR_TEXT = "Send client to Frontera EHR";
export const CLIENT_ALREADY_IN_FRONTERA_EHR_TEXT = "This client is already in Frontera EHR";
const GENERIC_ERROR_TEXT = "We couldn't send this client to Frontera EHR. Please try again.";

interface SendClientToFronteraEhrButtonProps {
  reportId: string;
  reportType: ApiReportReportTypeChoices | null | undefined;
  isReportCompleted: boolean;
  learnerExternalId: string | null | undefined;
  refetchReport: () => Promise<unknown>;
}

export const SendClientToFronteraEhrButton: React.FC<SendClientToFronteraEhrButtonProps> = ({
  reportId,
  reportType,
  isReportCompleted,
  learnerExternalId,
  refetchReport,
}) => {
  const { isFronteraEhrUser, isEhrSyncCleared } = useAdminData();
  const [dialogOpen, setDialogOpen] = useState(false);
  // Holds a successful send until the refetched report carries the link.
  const [sentReportId, setSentReportId] = useState<string | null>(null);
  // Render-closure `isPending` lets two clicks in one frame both send.
  const sendingRef = useRef(false);
  const { isPending, mutateAsync } = useSendClientToFronteraEhrMutation({});

  if (!isFronteraEhrUser || reportType !== ApiReportReportTypeChoices.DiagnosisEvaluation || !isReportCompleted)
    return null;

  const isAlreadyInFronteraEhr = !!learnerExternalId || sentReportId === reportId;
  const disabled = isAlreadyInFronteraEhr || !isEhrSyncCleared || isPending;

  let tooltipText: string | null = null;
  if (isAlreadyInFronteraEhr) {
    tooltipText = CLIENT_ALREADY_IN_FRONTERA_EHR_TEXT;
  } else if (!isEhrSyncCleared) {
    tooltipText = `${EHR_SYNC_OUTBOUND_GATED_MESSAGE} Contact support to get started.`;
  }

  const sendClient = async () => {
    if (disabled || sendingRef.current) return;
    sendingRef.current = true;
    let shouldRefetch = false;
    try {
      const data = await mutateAsync({ reportId });
      const result = data?.sendClientToFronteraEhr;
      shouldRefetch = !!result?.status;
      if (result?.status) {
        setSentReportId(reportId);
        notifySuccess("Client sent to Frontera EHR. Staff can assign them from the Unassigned clinic.");
      } else {
        notifyError(result?.message || GENERIC_ERROR_TEXT);
      }
    } catch (error) {
      // FDM may have linked the client before the connection dropped.
      shouldRefetch = true;
      datadogRum.addError(error, { context: "Error sending client to Frontera EHR" });
      notifyError(GENERIC_ERROR_TEXT);
    } finally {
      sendingRef.current = false;
    }
    setDialogOpen(false);
    if (shouldRefetch) await refetchReport();
  };

  const button = (
    <Button
      text={
        <div className="flex items-center gap-1">
          {isPending ? <Loader className="h-4 w-4" /> : null}
          {SEND_CLIENT_TO_FRONTERA_EHR_TEXT}
        </div>
      }
      disabled={disabled}
      appearance="primary"
      onClick={() => setDialogOpen(true)}
      className="ring-green-400"
    />
  );

  return (
    <>
      <div className="inline-block ml-2">
        {tooltipText ? (
          <Tooltip text={tooltipText} showInfoIcon={false}>
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </div>
      <Dialog
        size="xl"
        open={dialogOpen}
        setOpen={setDialogOpen}
        title={SEND_CLIENT_TO_FRONTERA_EHR_TEXT}
        primaryButton={{
          text: "Continue",
          onClick: sendClient,
          disabled: isPending,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setDialogOpen(false),
        }}
      >
        <>
          <Paragraph displayType="normal" colorType="secondary">
            This will send the client&apos;s details to Frontera EHR: name, date of birth, gender, guardians and home
            address when present.
          </Paragraph>
          <Paragraph displayType="normal" colorType="secondary" className="my-4">
            A new client will be created in the Unassigned clinic for your staff to assign. Would you like to proceed?
          </Paragraph>
        </>
      </Dialog>
    </>
  );
};
